import cx from 'classnames';
import { ChevronRight } from 'lucide-react';
import { useContext, useEffect, useRef } from 'react';
import Chip from '@/common/Chip';
import MenuOptionLabel from '~/constants/srp/MenuOptionLabel';
import ModalContext from '~/context/srp/ModalContext';
import SearchContext from '~/context/srp/SearchContext';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import {
  containerNegativeMargin,
  containerPadding
} from '../MainLayout.styles';

enum QuickMenuKey {
  PricePayment = 'price-payment',
  MakeModel = 'make-model',
  BodyType = 'body-type'
}

type MenuOption = {
  id: MenuOptionLabel;
  key: QuickMenuKey;
  label: string;
};

export const menuOptions: MenuOption[] = [
  {
    id: MenuOptionLabel.PricePayment,
    key: QuickMenuKey.PricePayment,
    label: 'Price'
  },
  {
    id: MenuOptionLabel.MakeModel,
    key: QuickMenuKey.MakeModel,
    label: 'Make/Model'
  },
  {
    id: MenuOptionLabel.BodyType,
    key: QuickMenuKey.BodyType,
    label: 'Body Type'
  }
];

function QuickMenu() {
  const { isFilterModalOpen, setFilterModalOpen } = useContext(ModalContext);
  const { setExpandOnOpen } = useContext(SearchContext);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleMenuOptionClick = (option: MenuOptionLabel) => {
    setExpandOnOpen(option);
    setFilterModalOpen(true);
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollLeft = 0;
  }, [isFilterModalOpen]);

  return (
    <div
      className={cx(
        containerNegativeMargin,
        'max-w-[100vw] overflow-hidden',
        'bg-background-primary-subtle pb-xl'
      )}
      data-testid="srp-quick-menu"
    >
      <div
        className={cx(containerPadding, 'no-scrollbar flex overflow-auto')}
        ref={scrollRef}
      >
        <div className="flex shrink-0 items-center gap-md">
          {menuOptions.map(({ id, key, label }) => (
            <Chip
              key={key}
              size="default"
              shape="pill"
              icon={<ChevronRight size={16} />}
              data-adobe-id={toLowerKebabCase('filter-menu', label)}
              data-analytics-id="Filter Menu"
              data-analytics-attributes={JSON.stringify({
                item: 'shortcut button',
                label,
                source: 'page'
              })}
              data-testid="menu-option"
              testId={`menu-option-${key}`}
              onClick={() => {
                handleMenuOptionClick(id);
              }}
            >
              <span className="pl-sm">{label}</span>
            </Chip>
          ))}
        </div>
      </div>
    </div>
  );
}

export default withErrorBoundary(QuickMenu, { section: 'SRP QuickMenu' });
